import {
  Box,
  Modal,
  Typography,
  IconButton,
  TextField,
  CircularProgress,
  Button
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import SendIcon from "@mui/icons-material/Send";
import DescriptionIcon from "@mui/icons-material/Description";
import HistoryIcon from "@mui/icons-material/History";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import { InputAdornment } from "@mui/material";
import "./Chatbot.css";
import Loader from "../../common/Loader";
import BILens from "./BILens";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Postrequestcall } from "../../request/Postrequest";
import {
  RAG_FETCH_CHATBOT_HISTORY,
  RAG_FETCH_CHATBOT_HISTORY_ID_DETAILS
} from "../../constant/Apipath";

export default function Chatbot({
  openChatbot,
  setOpenChatbot,
  chatbotResponse,
  chatbotLoader,
  callAsk,
  setChatbotResponse,
  clearCurrentChatThread,
  setCurrentChatHistoryId,
  normalizeChatbotHistoryRecord
}) {
  const HISTORY_PAGE_SIZE = 20;
  const [question, setQuestion] = useState("");
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyMoreLoading, setHistoryMoreLoading] = useState(false);
  const [selectedHistoryLoading, setSelectedHistoryLoading] = useState(false);
  const [isLensOpen, setIsLensOpen] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyItems, setHistoryItems] = useState([]);
  const [historySearch, setHistorySearch] = useState("");
  const [historyPageNr, setHistoryPageNr] = useState(1);
  const [historyHasMore, setHistoryHasMore] = useState(true);
  const chatBodyRef = useRef(null);
  const historyListRef = useRef(null);
  const prevHistoryLengthRef = useRef(0);
  const historyRequestIdRef = useRef(0);
  const historyOpenLoadSignatureRef = useRef("");

  const { loginData } = useSelector((state) => state?.main);
  const initialSuggestions = ["What is BuildId?", "How can I use BuildId to grow my career?"];
  const normalizeLoadedHistoryRecord =
    typeof normalizeChatbotHistoryRecord === "function"
      ? normalizeChatbotHistoryRecord
      : (item, fallbackId) => {
          const answerValue = item?.answer ?? item?.source ?? item?.Answer ?? "";
          let answer = "";
          let sources = [];

          const extractFromParsedObject = (parsedItem) => {
            if (!parsedItem || typeof parsedItem !== "object") {
              return;
            }

            answer = parsedItem?.Answer ?? parsedItem?.answer ?? answer;

            const rawSources = parsedItem?.Sources ?? parsedItem?.sources ?? [];
            if (Array.isArray(rawSources)) {
              sources = rawSources;
            } else if (typeof rawSources === "string") {
              try {
                const parsedSources = JSON.parse(rawSources);
                sources = Array.isArray(parsedSources) ? parsedSources : sources;
              } catch (error) {
                sources = sources;
              }
            }
          };

          if (typeof answerValue === "string") {
            try {
              const parsed = JSON.parse(answerValue);
              const parsedItem = Array.isArray(parsed) ? parsed[0] : parsed;
              if (parsedItem && typeof parsedItem === "object") {
                extractFromParsedObject(parsedItem);
              } else {
                answer = answerValue;
              }
            } catch (error) {
              answer = answerValue;
            }
          } else if (Array.isArray(answerValue) && answerValue.length > 0) {
            extractFromParsedObject(answerValue[0]);
          }

          if ((!sources || sources.length === 0) && typeof item?.source === "string") {
            try {
              const parsedSource = JSON.parse(item.source);
              const parsedSourceItem = Array.isArray(parsedSource) ? parsedSource[0] : parsedSource;
              if (Array.isArray(parsedSource)) {
                sources = parsedSource;
              } else if (parsedSourceItem && typeof parsedSourceItem === "object") {
                extractFromParsedObject(parsedSourceItem);
              }
            } catch (error) {
              // Keep the sources array empty if the fallback source field is malformed.
            }
          }

          return {
            id: item?.id ?? item?.historyDetailId ?? fallbackId ?? Date.now(),
            question: item?.question ?? item?.description ?? "",
            answer: answer || "No answer available.",
            sources,
            isPending: false,
            historyDetailId: item?.historyDetailId ?? item?.id ?? fallbackId ?? 0
          };
        };

  // Normalize history so we can render previous + new chats
  let history = [];
  if (Array.isArray(chatbotResponse)) {
    history = chatbotResponse;
  } else if (chatbotResponse) {
    const responseData =
      chatbotResponse?.data?.data ?? chatbotResponse?.data ?? chatbotResponse ?? {};
    history = [
      {
        question: responseData?.question || "",
        answer: responseData?.answer || "No answer available.",
        sources: Array.isArray(responseData?.sources) ? responseData.sources : []
      }
    ];
  }
  // Only show the big loader on the first request (when there is no chat yet).
  const showInitialLoader = chatbotLoader && history.length === 0;
  useEffect(() => {
    if (!openChatbot) {
      prevHistoryLengthRef.current = history.length;
      return;
    }

    const hasNewItem = history.length > prevHistoryLengthRef.current;
    const lastItem = history[history.length - 1];
    const newQuestionSent = hasNewItem && lastItem?.isPending === true;

    if (!newQuestionSent) {
      prevHistoryLengthRef.current = history.length;
      return;
    }

    const t = setTimeout(() => {
      const chatBodyEl = chatBodyRef.current;
      if (!chatBodyEl) return;
      chatBodyEl.scrollTo({ top: chatBodyEl.scrollHeight, behavior: "smooth" });
    }, 50);

    prevHistoryLengthRef.current = history.length;
    return () => clearTimeout(t);
  }, [openChatbot, history]);

  useEffect(() => {
    if (!isHistoryOpen) {
      historyOpenLoadSignatureRef.current = "";
      return;
    }

    const signature = `${historySearch.trim()}|open`;
    if (historyOpenLoadSignatureRef.current === signature) {
      return;
    }
    historyOpenLoadSignatureRef.current = signature;

    const t = setTimeout(() => {
      loadChatHistory({ pageNr: 1, append: false, fullSearch: historySearch });
    }, 300);

    return () => clearTimeout(t);
  }, [historySearch, isHistoryOpen]);

  const handleQuestionChange = (event) => {
    setQuestion(event.target.value);
  };

  const handleClose = () => {
    setIsHistoryOpen(false);
    setCurrentChatHistoryId?.(null);
    clearCurrentChatThread?.();
    setOpenChatbot(false);
  };

  const handleNewChat = () => {
    setIsHistoryOpen(false);
    setQuestion("");
    setCurrentChatHistoryId?.(null);
    clearCurrentChatThread?.();
  };

  const dedupeHistoryItems = (items) => {
    const map = new Map();
    items.forEach((item) => {
      const key =
        item?.id ?? item?.historyDetailId ?? `${item?.description ?? ""}-${item?.question ?? ""}`;
      map.set(key, item);
    });
    return Array.from(map.values());
  };

  const loadChatHistory = async ({ fullSearch = "", pageNr = 1, append = false } = {}) => {
    const requestId = ++historyRequestIdRef.current;
    try {
      const userId = loginData?.userInformation?.UserId ?? 0;
      const token = loginData?.accessToken ?? "";
      if (!userId || !token) {
        if (!append) {
          setHistoryItems([]);
        }
        setHistoryError("Unable to load history.");
        return;
      }
      if (append) {
        setHistoryMoreLoading(true);
      } else {
        setHistoryLoading(true);
        setHistoryError("");
      }
      const payload = {
        PageNr: pageNr,
        NrOfRecPerPage: HISTORY_PAGE_SIZE,
        FullSearch: fullSearch?.trim() ?? "",
        UserId: userId,
        IncludeRecordNr: "true",
        TypeOfObjectReturned: "",
        SearchList: [{ userId: Number(userId) }],
        SortList: [
          {
            FieldName: "Id",
            Direction: "desc"
          }
        ]
      };
      const response = await Postrequestcall(RAG_FETCH_CHATBOT_HISTORY_ID_DETAILS, payload, token);
      if (requestId !== historyRequestIdRef.current) {
        return;
      }

      const data = response?.data?.data ?? response?.data ?? response ?? {};
      const items = Array.isArray(data) ? data : [];
      const totalRecords = Number(
        response?.data?.nrOfRecords ?? response?.nrOfRecords ?? data?.nrOfRecords ?? 0
      );
      const nextItems = append ? dedupeHistoryItems([...historyItems, ...items]) : items;

      setHistoryItems(nextItems);
      setHistoryPageNr(pageNr);
      setHistoryHasMore(
        totalRecords > 0
          ? pageNr * HISTORY_PAGE_SIZE < totalRecords
          : items.length === HISTORY_PAGE_SIZE
      );
      if (!append && historyListRef.current) {
        historyListRef.current.scrollTop = 0;
      }
    } catch (e) {
      setHistoryError("Failed to load history.");
      if (!append) {
        setHistoryItems([]);
      }
    } finally {
      if (requestId === historyRequestIdRef.current) {
        setHistoryLoading(false);
        setHistoryMoreLoading(false);
      }
    }
  };

  const handleToggleHistory = () => {
    const next = !isHistoryOpen;
    setIsHistoryOpen(next);
  };

  const handleHistoryScroll = async (event) => {
    const el = event.currentTarget;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (!nearBottom || historyLoading || historyMoreLoading || !historyHasMore) {
      return;
    }

    const nextPage = historyPageNr + 1;
    await loadChatHistory({
      pageNr: nextPage,
      append: true,
      fullSearch: historySearch
    });
  };

  const getChatHistorydetails = async (historyId) => {
    setSelectedHistoryLoading(true);
    try {
      let payload = {
        PageNr: 1,
        NrOfRecPerPage: 20,
        FullSearch: "",
        UserId: loginData?.userInformation?.UserId ?? 0,
        IncludeRecordNr: "true",
        TypeOfObjectReturned: "",
        SearchList: [
          {
            userId: Number(loginData?.userInformation?.UserId ?? 0),
            historyDetailId: Number(historyId)
          }
        ],
        SortList: [
          {
            FieldName: "Id",
            Direction: "asc"
          }
        ]
      };
      const getResponse = await Postrequestcall(
        RAG_FETCH_CHATBOT_HISTORY,
        payload,
        loginData?.accessToken ?? ""
      );
      const data = getResponse?.data?.data ?? getResponse?.data ?? getResponse ?? {};
      const historyRows = Array.isArray(data) ? data : data ? [data] : [];
      const normalizedHistory = historyRows
        .map((item, index) => normalizeLoadedHistoryRecord(item, historyId ?? index))
        .filter((item) => item?.question || item?.answer);

      if (normalizedHistory.length === 0) {
        return;
      }

      const selectedThreadId =
        normalizedHistory[0]?.historyDetailId ??
        historyRows[0]?.historyDetailId ??
        historyRows[0]?.id ??
        Number(historyId) ??
        null;

      setCurrentChatHistoryId?.(selectedThreadId ? Number(selectedThreadId) : null);
      setChatbotResponse?.(normalizedHistory);
      setOpenChatbot(true);
    } catch (error) {
    } finally {
      setSelectedHistoryLoading(false);
    }
  };

  const handleSendClick = async () => {
    const trimmed = question.trim();
    if (!trimmed) {
      return;
    }
    setQuestion("");
    await callAsk(trimmed);
  };

  const handleSuggestionClick = async (suggestion) => {
    await callAsk(suggestion);
  };

  const showInitialView = history.length === 0;

  return (
    <Modal open={openChatbot} onClose={handleClose}>
      <Box className="modal-overlay">
        <Box
          className="chat-modal"
          sx={{
            borderRadius: { xs: 0, sm: 4 },
            border: "none",
            boxShadow: "0 24px 48px rgba(0,0,0,0.2)",
            background: "#f8fafc"
          }}>
          {/* Header */}
          <Box
            className="chatbot-header"
            sx={{
              background: "linear-gradient(90deg, #002045 0%, #003473 100%)",
              color: "#fff",
              display: "flex",
              justifyContent: "space-between",
              padding: "16px 24px",
              alignItems: "center",
              borderTopLeftRadius: { xs: 0, sm: "16px" },
              borderTopRightRadius: { xs: 0, sm: "16px" },
              boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
              position: "relative",
              zIndex: 10
            }}>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Box
                className="bot-icon"
                sx={{
                  background: "linear-gradient(135deg, #fdc003 0%, #e0aa00 100%)",
                  padding: "10px",
                  borderRadius: "50%",
                  color: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 12px rgba(253, 192, 3, 0.4)"
                }}>
                <SmartToyIcon />
              </Box>

              <Box>
                <Typography
                  sx={{ fontWeight: 700, fontSize: "18px", letterSpacing: "0.3px", mb: 0.2 }}>
                  BuildID Assistant
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: "#10b981",
                      boxShadow: "0 0 8px rgba(16, 185, 129, 0.8)"
                    }}
                  />
                  <Typography
                    sx={{ fontSize: "13px", color: "rgba(255,255,255,0.85)", fontWeight: 500 }}>
                    Always available to help
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                sx={{
                  color: "#fff",
                  bgcolor: "rgba(255,255,255,0.1)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.2)" }
                }}
                onClick={handleToggleHistory}
                aria-label="open chat history">
                <HistoryIcon fontSize="small" />
              </IconButton>
              <IconButton
                sx={{
                  color: "#fff",
                  bgcolor: "rgba(255,255,255,0.1)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.2)" }
                }}
                onClick={handleClose}
                aria-label="close chatbot">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {showInitialLoader ? (
            <div
              style={{
                minHeight: "500px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}>
              <Loader></Loader>
            </div>
          ) : (
            <>
              {/* Chat Body */}
              <Box
                className={`chat-body ${showInitialView ? "chat-body--empty" : ""}`}
                ref={chatBodyRef}
                sx={{ position: "relative" }}>
                {selectedHistoryLoading && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      zIndex: 3,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                      gap: 1,
                      backgroundColor: "rgba(255,255,255,0.7)",
                      backdropFilter: "blur(2px)"
                    }}>
                    <CircularProgress size={28} sx={{ color: "#002045" }} />
                    <Typography sx={{ color: "#334155", fontSize: 14, fontWeight: 500 }}>
                      Loading conversation...
                    </Typography>
                  </Box>
                )}
                <Box
                  sx={{
                    opacity: selectedHistoryLoading ? 0.35 : 1,
                    pointerEvents: selectedHistoryLoading ? "none" : "auto"
                  }}>
                  {showInitialView ? (
                    <Box className="chat-empty-state">
                      <Box className="chat-empty-icon">
                        <SmartToyIcon />
                      </Box>
                      <Typography className="chat-empty-title">Welcome to Chat Bot</Typography>
                      <Typography className="chat-empty-subtitle">
                        I&apos;m here to answer questions related to BuildId. Start a conversation
                        below.
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.5,
                          width: "100%",
                          mt: 4,
                          maxWidth: 480
                        }}>
                        {/* BI Lens Special Card */}
                        <Box
                          onClick={() => setIsLensOpen(true)}
                          sx={{
                            p: 1.5,
                            borderRadius: 3,
                            border: "1px solid",
                            borderColor: "primary.light",
                            bgcolor: "#f0f4ff",
                            cursor: "pointer",
                            transition: "all 0.15s",
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            "&:hover": {
                              borderColor: "primary.main",
                              bgcolor: "#e6eeff",
                              transform: "translateY(-1px)",
                              boxShadow: "0 4px 10px rgba(0,32,69,0.06)"
                            }
                          }}>
                          <Box
                            sx={{
                              p: 1,
                              borderRadius: 2,
                              bgcolor: "#d6e4ff",
                              color: "primary.main",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center"
                            }}>
                            <CameraAltIcon />
                          </Box>
                          <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 700,
                                color: "#002045",
                                lineHeight: 1.2,
                                textAlign: "left"
                              }}>
                              Open BI Lens
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary", textAlign: "left", mt: 0.5 }}>
                              Upload an image to identify products or landmarks.
                            </Typography>
                          </Box>
                        </Box>

                        {/* FAQ 1 */}
                        <Box
                          onClick={() => handleSuggestionClick(initialSuggestions[0])}
                          sx={{
                            p: 1.5,
                            borderRadius: 3,
                            border: "1px solid",
                            borderColor: "divider",
                            bgcolor: "#fff",
                            cursor: "pointer",
                            transition: "all 0.15s",
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            "&:hover": {
                              borderColor: "#c4c6cf",
                              bgcolor: "#f8f9fa",
                              transform: "translateY(-1px)",
                              boxShadow: "0 4px 10px rgba(0,0,0,0.03)"
                            }
                          }}>
                          <Box
                            sx={{
                              p: 1,
                              borderRadius: 2,
                              bgcolor: "#f0f4f8",
                              color: "#5f6775",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center"
                            }}>
                            <SearchIcon />
                          </Box>
                          <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                color: "#002045",
                                lineHeight: 1.2,
                                textAlign: "left"
                              }}>
                              What is BuildId?
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary", textAlign: "left", mt: 0.5 }}>
                              Learn about the platform and its core features.
                            </Typography>
                          </Box>
                        </Box>

                        {/* FAQ 2 */}
                        <Box
                          onClick={() => handleSuggestionClick(initialSuggestions[1])}
                          sx={{
                            p: 1.5,
                            borderRadius: 3,
                            border: "1px solid",
                            borderColor: "divider",
                            bgcolor: "#fff",
                            cursor: "pointer",
                            transition: "all 0.15s",
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            "&:hover": {
                              borderColor: "#c4c6cf",
                              bgcolor: "#f8f9fa",
                              transform: "translateY(-1px)",
                              boxShadow: "0 4px 10px rgba(0,0,0,0.03)"
                            }
                          }}>
                          <Box
                            sx={{
                              p: 1,
                              borderRadius: 2,
                              bgcolor: "#fdf4db",
                              color: "#b27b00",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center"
                            }}>
                            <SmartToyIcon />
                          </Box>
                          <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                color: "#002045",
                                lineHeight: 1.2,
                                textAlign: "left"
                              }}>
                              Grow your career
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary", textAlign: "left", mt: 0.5 }}>
                              Discover how BuildId can help you advance.
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    history.map((item, idx) => (
                      <Box key={idx} sx={{ mb: 2 }}>
                        {item.question && (
                          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.5 }}>
                            <Box
                              sx={{
                                background: "linear-gradient(135deg, #002045 0%, #003066 100%)",
                                color: "#fff",
                                p: 2,
                                borderRadius: "20px",
                                borderBottomRightRadius: "4px",
                                maxWidth: "85%",
                                boxShadow: "0 4px 12px rgba(0,32,69,0.15)"
                              }}>
                              <Typography
                                sx={{
                                  whiteSpace: "pre-line",
                                  color: "#fff",
                                  fontSize: "15px",
                                  lineHeight: 1.5
                                }}>
                                {item.question}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        {(item?.isPending || item?.answer) && (
                          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 1.5 }}>
                            <Box
                              sx={{
                                background: "#fff",
                                color: "#1e293b",
                                p: 2,
                                borderRadius: "20px",
                                borderBottomLeftRadius: "4px",
                                maxWidth: "85%",
                                border: "1px solid #e2e8f0",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.03)"
                              }}>
                              {item?.isPending ? (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                  <CircularProgress size={16} sx={{ color: "#002045" }} />
                                  <Typography
                                    sx={{
                                      whiteSpace: "pre-line",
                                      color: "#64748b",
                                      fontSize: "14px",
                                      fontStyle: "italic"
                                    }}>
                                    Thinking...
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography
                                  sx={{
                                    whiteSpace: "pre-line",
                                    color: "#1e293b",
                                    fontSize: "15px",
                                    lineHeight: 1.6
                                  }}>
                                  {item.answer || "No answer available."}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        )}
                        {Array.isArray(item.sources) && item.sources.length > 0 && (
                          <Box className="citations-wrapper">
                            <Typography className="citations-title">Citations</Typography>
                            <Box className="action-list">
                              {item.sources.map((source, index) => (
                                <Box
                                  key={source?.id || `${source?.title}-${index}`}
                                  className="action-item">
                                  <DescriptionIcon fontSize="small" />
                                  <Box>
                                    <Typography className="action-title">
                                      {source?.title || source?.Title || `Source ${index + 1}`}
                                    </Typography>
                                    <Typography className="action-desc">
                                      {source?.snippet ||
                                        source?.Snippet ||
                                        source?.description ||
                                        source?.Description ||
                                        "No snippet available."}
                                    </Typography>
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    ))
                  )}
                </Box>
              </Box>

              {/* Footer */}
              <Box className="chat-footer">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%" }}>
                  <IconButton
                    onClick={() => setIsLensOpen(true)}
                    disabled={chatbotLoader}
                    sx={{
                      color: "text.secondary",
                      bgcolor: "grey.50",
                      border: "1px solid",
                      borderColor: "grey.300",
                      borderRadius: "12px",
                      height: 42,
                      width: 42,
                      "&:hover": { bgcolor: "grey.100" }
                    }}>
                    <CameraAltIcon />
                  </IconButton>

                  <TextField
                    fullWidth
                    placeholder={
                      showInitialView ? "Ask me anything about BuildId..." : "Ask follow-up..."
                    }
                    variant="outlined"
                    size="small"
                    value={question}
                    onChange={handleQuestionChange}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSendClick();
                      }
                    }}
                    InputProps={{
                      sx: {
                        borderRadius: "12px",
                        backgroundColor: "#fff",
                        height: 42,

                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "1px solid #c4c6cf"
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          border: "1px solid #c4c6cf"
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          border: "1px solid #002045"
                        }
                      }
                    }}
                  />

                  <IconButton
                    onClick={handleSendClick}
                    disabled={chatbotLoader || !question.trim()}
                    sx={{
                      color: question.trim() ? "#fff" : "text.disabled",
                      bgcolor: question.trim() ? "#002045" : "action.disabledBackground",
                      borderRadius: "12px",
                      height: 42,
                      width: 42,
                      "&:hover": {
                        bgcolor: question.trim() ? "#001530" : "action.disabledBackground"
                      }
                    }}>
                    <SendIcon fontSize="small" sx={{ ml: 0.5 }} />
                  </IconButton>
                </Box>
              </Box>
              {isLensOpen && <BILens open={isLensOpen} onClose={() => setIsLensOpen(false)} />}
            </>
          )}

          {/* History Sidebar */}
          <Box className={`chat-history-sidebar ${isHistoryOpen ? "open" : ""}`}>
            <Box className="chat-history-header">
              <Typography className="chat-history-title">Chat History</Typography>
              <Box className="chat-history-actions">
                <IconButton
                  size="small"
                  aria-label="new chat"
                  onClick={handleNewChat}
                  disabled={historyLoading}>
                  <AddIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label="close history"
                  onClick={() => setIsHistoryOpen(false)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            <Box className="chat-history-search">
              <SearchIcon fontSize="small" />
              <input
                value={historySearch}
                onChange={(e) => setHistorySearch(e.target.value)}
                placeholder="Search conversations..."
                className="chat-history-search-input"
              />
            </Box>

            <Box ref={historyListRef} className="chat-history-list" onScroll={handleHistoryScroll}>
              {historyLoading ? (
                <Box className="chat-history-loading">
                  <CircularProgress size={18} sx={{ color: "#002045" }} />
                  <Typography sx={{ fontSize: 13, color: "#5f6775" }}>Loading...</Typography>
                </Box>
              ) : historyError ? (
                <Typography className="chat-history-empty">{historyError}</Typography>
              ) : historyItems.length === 0 ? (
                <Typography className="chat-history-empty">
                  {historySearch.trim() ? "No matching conversations found." : "No history found."}
                </Typography>
              ) : (
                historyItems.map((q, index) => (
                  <button
                    key={q?.id ?? `${q?.description ?? q?.question ?? "history"}-${index}`}
                    className="chat-history-item"
                    onClick={async () => {
                      setIsHistoryOpen(false);
                      await getChatHistorydetails(q?.id);
                    }}>
                    <span className="chat-history-item-icon">
                      <SmartToyIcon fontSize="small" />
                    </span>
                    <span className="chat-history-item-text">
                      {q?.description || q?.question || "Conversation"}
                    </span>
                  </button>
                ))
              )}
              {historyMoreLoading && (
                <Box className="chat-history-loading">
                  <CircularProgress size={16} sx={{ color: "#002045" }} />
                  <Typography sx={{ fontSize: 13, color: "#5f6775" }}>Loading more...</Typography>
                </Box>
              )}
              {!historyLoading &&
                !historyMoreLoading &&
                historyItems.length > 0 &&
                !historyHasMore && (
                  <Typography className="chat-history-empty" sx={{ pt: 1 }}>
                    End of history
                  </Typography>
                )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}
